/**
 * Hybrid AES-CFB + RSA-OAEP encryption using node-forge
 */
import forge from 'node-forge';

/**
 * Encrypts a payload using hybrid encryption:
 * 1. Generate random AES key + IV
 * 2. Encrypt payload with AES-CFB
 * 3. Encrypt AES key with RSA-OAEP using the server's public key
 *
 * @param {string} publicKeyPem - RSA public key in PEM format
 * @param {object} payload - The data object to encrypt
 * @returns {{ encrypted_key: string, payload: string, iv: string }} Base64-encoded encrypted parts
 */
export function encryptPayload(publicKeyPem, payload) {
  const aesKey = forge.random.getBytesSync(32);
  const iv = forge.random.getBytesSync(16);

  const cipher = forge.cipher.createCipher('AES-CFB', aesKey);
  cipher.start({ iv });
  cipher.update(forge.util.createBuffer(JSON.stringify(payload), 'utf8'));
  cipher.finish();
  const encryptedPayload = cipher.output.getBytes();

  const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
  const encryptedKey = publicKey.encrypt(aesKey, 'RSA-OAEP', {
    md: forge.md.sha256.create(),
    mgf1: { md: forge.md.sha256.create() },
  });

  return {
    encrypted_key: forge.util.encode64(encryptedKey),
    payload: forge.util.encode64(encryptedPayload),
    iv: forge.util.encode64(iv),
  };
}
